import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'redux/types';
import { createPortal } from 'react-dom';
import colors from 'app/styles/theme/colors';
import useClickOutside from 'app/hooks/useClickOutside';
import InviteModal from 'app/pages/Gamification/components/InviteModal';
import { MGM_CHALLENGE_STUB } from 'app/components/Mgm/mgmChallengeStub';
import {
  BookOutlineIcon,
  InviteOutlineIcon,
  LogoutOutlineIcon,
  OpenInNewOutlineIcon,
  UserOutlineIcon,
} from '../../../../icons';
import { PROFILE_EDIT_MENU_LABEL, PROFILE_MGM_MENU_LABEL } from '../../types';
import { trackMenuProfileMgmClicked } from '../../trackMenuEvents';
import * as S from './styles';
import { ROUTES_PATH } from 'app/constants/Routes';

export type ProfileMenuAccountItem = {
  id: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  disabled?: boolean;
  disabledTooltip?: string;
};

type ProfileMenuProps = {
  onNavigate: (path: string) => void;
  accountItems: ProfileMenuAccountItem[];
  showMgmInviteAction?: boolean;
  tooltip?: string;
};

/** "Walisson Camargo" → "WC". Sem nome, cai no fallback genérico "JU". */
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'JU';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

const ProfileMenu = ({
  onNavigate,
  accountItems,
  showMgmInviteAction = false,
  tooltip = 'Perfil',
}: ProfileMenuProps) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [open, setOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, bottom: 0 });

  const handleClose = useCallback(() => setOpen(false), []);
  const handleCloseInviteModal = useCallback(() => setIsInviteModalOpen(false), []);
  const clickOutsideIgnoredIds = isInviteModalOpen
    ? ['profile-menu-portal', 'profile-menu-invite-modal-portal']
    : ['profile-menu-portal'];
  const containerRef = useClickOutside(handleClose, clickOutsideIgnoredIds);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleSelect = (path: string, externalLink = false) => {
    if (externalLink || path.startsWith('http')) {
      const win = window.open(path, '_blank', 'noopener,noreferrer');
      if (win) win.opener = null;
    } else {
      onNavigate(path);
    }
    handleClose();
  };

  const handleMgmClick = () => {
    trackMenuProfileMgmClicked();
    setIsInviteModalOpen(true);
    handleClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen((prev) => !prev);
    }
  };

  const userName = user?.name ?? '';
  const userEmail = user?.email ?? '';

  useLayoutEffect(() => {
    if (!open || !containerRef.current) {
      return;
    }

    const sidebarColumn = containerRef.current.closest('[data-sidebar-column]');
    const leftPx = sidebarColumn
      ? sidebarColumn.getBoundingClientRect().right
      : containerRef.current.getBoundingClientRect().right;

    setMenuPosition({
      left: leftPx,
      bottom: 24,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleResize = () => handleClose();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open, handleClose]);

  // O painel vive num portal (document.body), então um onKeyDown no trigger
  // não alcança o foco dentro dele — Esc precisa ser ouvido no document.
  useEffect(() => {
    if (!open) return;
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      handleClose();
      triggerRef.current?.focus();
    };
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [open, handleClose]);

  const menuPanel = open ? (
    <S.Panel role="menu" aria-label="Sua conta" $left={menuPosition.left} $bottom={menuPosition.bottom}>
      <S.Top>
        <S.Avatar aria-hidden>
          {user?.pic ? (
            <S.AvatarImage src={user.pic} alt="" data-testid="profile-avatar-photo" />
          ) : (
            getInitials(userName)
          )}
        </S.Avatar>
        <S.Who>
          <S.Name>{userName}</S.Name>
          <S.Email>{userEmail}</S.Email>
        </S.Who>
        <S.EditButton
          type="button"
          role="menuitem"
          onClick={() => handleSelect(ROUTES_PATH.SETTINGS.PERFIL)}
        >
          {PROFILE_EDIT_MENU_LABEL}
        </S.EditButton>
      </S.Top>

      {accountItems.length > 0 && (
        <S.Group>
          <S.GroupLabel>Gestão da conta</S.GroupLabel>
          {accountItems.map((item) => (
            <S.Item
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              title={item.disabled ? item.disabledTooltip : undefined}
              aria-disabled={item.disabled}
              onClick={() => !item.disabled && handleSelect(item.path)}
            >
              {item.icon}
              <span>{item.label}</span>
            </S.Item>
          ))}
        </S.Group>
      )}

      {showMgmInviteAction && (
        <S.InviteBanner type="button" role="menuitem" onClick={handleMgmClick}>
          <S.InviteIcon aria-hidden>
            <InviteOutlineIcon size={15} color="currentColor" />
          </S.InviteIcon>
          {PROFILE_MGM_MENU_LABEL}
          <S.InviteArrow aria-hidden>
            <OpenInNewOutlineIcon size={13} color="currentColor" />
          </S.InviteArrow>
        </S.InviteBanner>
      )}

      <S.Footer>
        <S.HelpButton
          type="button"
          role="menuitem"
          title={ROUTES_PATH.EXTERNAL.MANUALS}
          onClick={() => handleSelect(ROUTES_PATH.EXTERNAL.MANUALS, true)}
        >
          <BookOutlineIcon size={16} color="currentColor" />
          Manuais de uso
        </S.HelpButton>

        <S.LogoutButton type="button" role="menuitem" onClick={() => handleSelect(ROUTES_PATH.AUTH.LOGOUT)}>
          Sair
          <LogoutOutlineIcon size={14} color="currentColor" />
        </S.LogoutButton>
      </S.Footer>
    </S.Panel>
  ) : null;

  return (
    <S.Wrapper ref={containerRef}>
      <S.IconWrapper
        ref={triggerRef}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={tooltip}
        data-tooltip-id="sidebar-icon-tooltip"
        data-tooltip-content={tooltip}
      >
        <S.Icon $active={open}>
          <UserOutlineIcon color={colors.designSystem.gray[800]} size={20} />
        </S.Icon>
      </S.IconWrapper>
      {open && createPortal(<div id="profile-menu-portal">{menuPanel}</div>, document.body)}
      {showMgmInviteAction &&
        isInviteModalOpen &&
        createPortal(
          <div id="profile-menu-invite-modal-portal">
            <InviteModal
              isOpen={isInviteModalOpen}
              challenge={MGM_CHALLENGE_STUB}
              onClose={handleCloseInviteModal}
            />
          </div>,
          document.body,
        )}
    </S.Wrapper>
  );
};

export default ProfileMenu;
